import { ColumnIndex } from "@tento-ui/components/sheet/column-index";
import type { RowLocalId } from "@tento-lona/sheets";

export namespace DecorationRenderController {
  export type Id = string;

  export type Frame = {
    id: Id;
    rowId: RowLocalId;
    anchorColumn: ColumnIndex.Key.Resolved.WithSubindex;
    xPx: number;
    yPx: number;
    widthPx: number;
    heightPx: number;
  };

  export type Op =
    | {
        type: "reveal";
        frame: Frame;
      }
    | {
        type: "update";
        frame: Frame;
      }
    | {
        type: "dismiss";
        id: Id;
      };

  export type Plan = {
    nextFrames: Map<Id, Frame>;
    ops: Op[];
  };

  export function computePlan(
    desiredIds: Set<Id>,
    measuredFramesById: Map<Id, Frame>,
    prevFrames: Map<Id, Frame>,
  ): Plan {
    const nextFrames = new Map<Id, Frame>();
    const ops: Op[] = [];

    for (const id of desiredIds) {
      const frame = measuredFramesById.get(id);
      if (!frame) {
        if (prevFrames.has(id)) {
          ops.push({ type: "dismiss", id });
        }
        continue;
      }

      nextFrames.set(id, frame);

      const prev = prevFrames.get(id);
      if (!prev) {
        ops.push({ type: "reveal", frame });
        continue;
      }

      const changed =
        prev.xPx !== frame.xPx ||
        prev.yPx !== frame.yPx ||
        prev.widthPx !== frame.widthPx ||
        prev.heightPx !== frame.heightPx ||
        prev.anchorColumn.key.offset !== frame.anchorColumn.key.offset ||
        prev.anchorColumn.subindex.toString() !==
          frame.anchorColumn.subindex.toString();

      if (changed) {
        ops.push({ type: "update", frame });
      }
    }

    for (const [id] of prevFrames) {
      if (!desiredIds.has(id)) {
        ops.push({ type: "dismiss", id });
      }
    }

    return { nextFrames, ops };
  }
}
